import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { Box, Container, Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import TextFieldComponent, { TextFieldType } from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import { EventType, Message, Severity } from '@professocial/professocialTypes/types/messageTypes'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import {
  UserLoginRequest
} from '@professocialApi/professocial-core-frontend-api/apis/AutorisationROApi'

import * as userActions from '../../../store/user/user.actions'
import { setMessage } from '../../../store/user/user.actions'
import ForgotPassword from './ForgotPassword'
import RegisterUser from './RegisterUser'
import { LoginProps } from '../../../screens/ProfessocialLogin'
import { useParams } from 'react-router'
import ResetPassword from './ResetPassword'
import { clearRequests } from '../../../store/common/common.actions'

const useStyles = makeStyles(() => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyItems: 'center',
    padding: '30px 80px',
    backgroundColor: '#fff',
    marginTop: '50px',
  },
  form: {
    width: '100%',
  },
  submit: {
    width: '100%',
  },
  link: {
    cursor: 'pointer',
  },
  grid: {
    display: 'flex',
    justifyContent: 'center',
  },
  dialog: {
    margin: '40px 20px 20px',
  },
}))

const Login: React.FC<LoginProps> = (props) => {
  const dispatch = useDispatch()
  const { token } = useParams<{ token: string }>()

  const dispatchUserLogin = useCallback((login: UserLoginRequest) => dispatch(userActions.userLoginInitiated(login)), [dispatch])
  const dispatchSetMessage = useCallback((message: Message) => dispatch(setMessage(message)), [dispatch])
  const dispatchClearRequests = useCallback(() => dispatch(clearRequests()), [dispatch])

  const classes = useStyles()
  const { t } = useTranslation()

  const getFingerprint = () => {
    let fingerprint = window.localStorage.getItem('fingerprint')
    if (!fingerprint) {
      const random = Math.floor(Math.random() * (10000000000 - 10000 + 1) + 10000)
      const navigatorInfo = window.navigator
      const screenInfo = window.screen
      fingerprint = navigatorInfo.mimeTypes.length.toString()
      fingerprint += navigatorInfo.userAgent.replace(/\D+/g, '')
      fingerprint += navigatorInfo.plugins.length
      fingerprint += screenInfo.height || ''
      fingerprint += screenInfo.width || ''
      fingerprint += screenInfo.pixelDepth || ''
      fingerprint += random
      window.localStorage.setItem('fingerprint', fingerprint)
    }
    return fingerprint
  }

  const fingerprint = getFingerprint()

  const [username, setUsername] = useState<string | undefined>('')
  const [password, setPassword] = useState<string | undefined>('')

  const [showForgotPasswortModal, setShowForgotPasswortModal] = useState<boolean>(false)
  const [showResetPasswortModal, setResetPasswortModal] = useState<boolean>(false)
  const [showRegisterModal, setShowRegisterModal] = useState<boolean>(false)

  const onLogin = () => {
    if (username && password) {
      const loginRequest: UserLoginRequest = {
        loginCredentialsInputDto: {
          username,
          password,
          fingerprint,
        },
      }
      dispatchUserLogin(loginRequest)
    } else {
      dispatchSetMessage({
        message: t('message:loginError'),
        severity: Severity.error,
        eventType: EventType.login,
      })
    }
  }

  const onKeyPress = (key: string) => {
    if (key === 'Enter') {
      onLogin()
    }
  }

  useEffect(() => {
    token && setResetPasswortModal(true)
  }, [token])

  const handleCloseResetPasswordModal = () => {
    setShowForgotPasswortModal(false)
    dispatchClearRequests()
  }

  return (
    <Container component='main' maxWidth='sm'>
      <div className={classes.paper}>
        <Typography variant='h4' style={{ marginBottom: '20px' }}>{t('login:welcome')}</Typography>
        <form className={classes.form} noValidate>
          <Box sx={{ pb: 2, pt: 2 }}>
            <TextFieldComponent
              required
              fullWidth
              id='username'
              name='username'
              autoComplete='username'
              autoFocus
              label={t('login:username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={(e) => onKeyPress(e.key)}
            />
          </Box>
          <Box sx={{ pb: 1 }}>
            <TextFieldComponent
              required
              fullWidth
              name='password'
              label={t('login:password')}
              type={TextFieldType.password}
              id='password'
              autoComplete='current-password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => onKeyPress(e.key)}
            />
          </Box>
          <Grid container sx={{ marginTop: '20px', marginBottom: '10px' }}>
            <Grid item xs={12} className={classes.grid}>
              <Typography
                className={classes.link}
                align='center'
                onClick={() => setShowForgotPasswortModal(true)}
              ><u>{t('login:forgotPassword')}</u>
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.grid}>
              <Typography
                className={classes.link}
                align='center'
                onClick={() => setShowRegisterModal(true)}
              ><u>{t('login:register')}</u>
              </Typography>
            </Grid>
          </Grid>
          <ButtonComponent
            className={classes.submit}
            onClick={onLogin}
            text={t('login:login')}
          />
        </form>
      </div>
      {showForgotPasswortModal &&
        <DialogComponent
          title={t('login:resetPassword')}
          content={
            <Box className={classes.dialog}>
              <ForgotPassword onClose={handleCloseResetPasswordModal} />
            </Box>
          }
          open={showForgotPasswortModal}
          onClose={handleCloseResetPasswordModal}
          onCancel={handleCloseResetPasswordModal}
          autoClose={false}
          maxWidth='sm'
          fullWidth
          closeIcon
        />}
      {showRegisterModal &&
        <DialogComponent
          content={
            <RegisterUser
              isOpenCallback={(value: boolean) => setShowRegisterModal(value)}
              registrationCustomerData={props.loginConfiguration?.registrationCustomerData}
            />
          }
          open={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          onCancel={() => setShowRegisterModal(false)}
          autoClose={false}
          scroll='body'
          closeIcon
        />}
      {showResetPasswortModal &&
        <DialogComponent
          title={t('login:resetPassword')}
          content={<ResetPassword onClose={() => setResetPasswortModal(false)} token={token}/>}
          open={showResetPasswortModal}
          onClose={() => setResetPasswortModal(false)}
          onCancel={() => setResetPasswortModal(false)}
          autoClose={false}
          maxWidth='sm'
          disableEscapeBackdropClick
          fullWidth
          closeIcon
        />}
    </Container>
  )
}
export default Login
